// A server-paged list of documents of a template family with the filter bar's rules, the sort and the search sent to the
// server (spec/dashboard.md section 6.1), the drafts of the family overlaid: the grid and the card pages share it.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import type { Document, Parameter } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useGame } from '../shell/GameContext';
import type { SortState } from '../ui/DataGrid';
import type { FilterRule } from '../ui/FilterBar';
import { useWorkspace, useWorkspaceVersion } from '../workspace/react';
import { entityKey } from '../workspace/Workspace';
import { useContent } from './ContentContext';
import { toServerFilter } from './actions';
import { heirsOf } from './widgets/context';

export interface DocumentListOptions {
  template: string;
  includeHeirs?: boolean;
  search?: string;
  rules?: FilterRule[];
  sort?: SortState | null;
  pageSize?: number;
  /** the parameters the rules are typed by (the family root's effective parameters by default) */
  params?: Parameter[];
}

export interface DocumentList {
  /** the rows: new drafts of the family first, then the server rows with their drafts overlaid */
  rows: Document[];
  /** the server rows as loaded (the bases) */
  items: Document[];
  total: number | null;
  loading: boolean;
  error: ApiError | null;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
  serverOf: (doc: Document) => Document;
  version: number;
}

function valueAtPath(doc: Document, field: string): unknown {
  let cur: unknown = doc;
  for (const seg of field.split('.')) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

/** The filter bar's rules applied client-side to a draft (the server never saw it): the comparators of `FilterBar`. */
export function matchesRules(doc: Document, rules: FilterRule[]): boolean {
  for (const r of rules) {
    if (r.field.startsWith('_')) continue;
    const v = valueAtPath(doc, r.field);
    const text = v === null || v === undefined ? '' : Array.isArray(v) ? v.join(',') : String(v);
    const numeric = typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)));
    const same = numeric && !Number.isNaN(Number(r.value)) ? Number(v) === Number(r.value) : typeof v === 'boolean' ? String(v) === r.value : text === r.value;
    switch (r.op) {
      case 'eq':
        if (!same) return false;
        break;
      case 'ne':
        if (same) return false;
        break;
      case 'gt':
        if (!(Number(v) > Number(r.value))) return false;
        break;
      case 'ge':
        if (!(Number(v) >= Number(r.value))) return false;
        break;
      case 'lt':
        if (!(Number(v) < Number(r.value))) return false;
        break;
      case 'le':
        if (!(Number(v) <= Number(r.value))) return false;
        break;
      case 'contains':
        if (!text.toLowerCase().includes(r.value.toLowerCase())) return false;
        break;
      case 'starts':
        if (!text.toLowerCase().startsWith(r.value.toLowerCase())) return false;
        break;
      case 'empty':
        if (text !== '' && !(Array.isArray(v) && v.length === 0)) return false;
        break;
      case 'notEmpty':
        if (text === '' || (Array.isArray(v) && v.length === 0)) return false;
        break;
    }
  }
  return true;
}

export function useDocumentList(opts: DocumentListOptions): DocumentList {
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const workspace = useWorkspace();
  const version = useWorkspaceVersion(workspace);
  const catalog = useContent();
  const includeHeirs = opts.includeHeirs ?? true;
  const root = catalog.templates.get(opts.template);
  const params = opts.params ?? root?.effectiveParameters ?? [];
  const heirs = useMemo(() => heirsOf(catalog, opts.template), [catalog, opts.template]);
  const family = useMemo(() => new Set([opts.template, ...(includeHeirs ? heirs.map((h) => h.name) : [])]), [opts.template, heirs, includeHeirs]);
  const serverRules = useMemo(() => toServerFilter(opts.rules ?? [], params), [opts.rules, params]);
  const query = useMemo(
    () => ({
      template: opts.template,
      includeHeirs: includeHeirs ? 'true' : undefined,
      limit: opts.pageSize ?? 50,
      search: opts.search || undefined,
      filter: serverRules.length > 0 ? JSON.stringify(serverRules) : undefined,
      sort: opts.sort ? `${opts.sort.key}:${opts.sort.dir}` : undefined,
    }),
    [opts.template, includeHeirs, opts.pageSize, opts.search, serverRules, opts.sort],
  );
  const [items, setItems] = useState<Document[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const key = `${branch}|${JSON.stringify(query)}|${workspace.head ?? ''}`;
  const last = useRef('');
  const seq = useRef(0);
  const load = useCallback(
    async (after: string | null, replace: boolean) => {
      const mine = ++seq.current;
      setLoading(true);
      try {
        const page = await client.data(ops.listDocuments, { path: { org, game: game.slug, branch }, query: { ...query, cursor: after ?? undefined } });
        if (mine !== seq.current) return; // a newer request superseded this one (the typed filter after the catalog loaded, a search keystroke)
        setItems((prev) => (replace ? page.items : [...prev, ...page.items]));
        setCursor(page.nextCursor);
        setTotal(page.total === null || page.total === undefined ? null : Number(page.total));
        setError(null);
        catalog.resolver.seed(page.items);
      } catch (e) {
        setError(e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), ''));
      } finally {
        setLoading(false);
      }
    },
    [client, org, game.slug, branch, query, catalog.resolver],
  );
  useEffect(() => {
    // the rules are typed by the template's parameters: wait for the catalog, else a `data.flag eq "1"` would go out as a string
    if (!root || last.current === key) return;
    last.current = key;
    void load(null, true);
  }, [key, load, root]);
  const rows = useMemo(() => {
    const drafts = workspace.all.filter((d) => d.kind === 'document' && family.has(d.template ?? ''));
    // new drafts never saw the server's filter: the rules apply to them here (a staged A/B override stays off the Overrides page)
    const fresh = drafts
      .filter((d) => d.status === 'new')
      .map((d) => d.current as unknown as Document)
      .filter((doc) => matchesRules(doc, opts.rules ?? []));
    const overlaid = items.map((doc) => (workspace.getDraft(entityKey('document', doc.id))?.current as unknown as Document | undefined) ?? doc);
    return [...fresh, ...overlaid];
  }, [items, workspace, family, version]); // eslint-disable-line react-hooks/exhaustive-deps
  const serverOf = useCallback((doc: Document) => items.find((d) => d.id === doc.id) ?? doc, [items]);
  return { rows, items, total, loading, error, hasMore: cursor !== null, loadMore: () => void load(cursor, false), reload: () => void load(null, true), serverOf, version };
}
